import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheck, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./About.css";

/* ── hero background ── */
import heroBg from "../assets/images/ppt_media_image20.jpeg";

/* ── intro image ── */
import introImg from "../assets/images/bakwenakit.png";

/* ── intro corner icon ── */
import iconLogo3 from "../assets/images/about/icon-logo3.png";

/* ── background video ── */
import bgVideo from "../assets/images/videos/landscape_video4.mp4";

/* ── tab panel images (local assets) ── */
import tabImg1 from "../assets/images/about/ppt_media_image22.jpg";
import tabImg2 from "../assets/images/about/ppt_media_image18.jpg";
import tabImg3 from "../assets/images/about/ppt_media_image17.jpg";

/* ── service SVG icons (reused for values) ── */
import iconTrust   from "../assets/images/services/shake-hands.svg";
import iconQuality from "../assets/images/services/quality-supervision.svg";
import iconHealth  from "../assets/images/services/health.svg";
import iconCrane   from "../assets/images/services/crane.svg";

/* ── partner logos ── */
import partner_1  from "../assets/images/partners/partner_01.jpg";
import partner_2  from "../assets/images/partners/partner_02.jpg";
import partner_3  from "../assets/images/partners/partner_03.jpg";
import partner_4  from "../assets/images/partners/partner_04.jpg";
import partner_5  from "../assets/images/partners/partner_05.jpg";
import partner_6  from "../assets/images/partners/partner_06.jpg";
import partner_8  from "../assets/images/partners/partner_08.jpg";
import partner_9  from "../assets/images/partners/partner_09.jpg";
import partner_10 from "../assets/images/partners/partner_10.jpg";
import partner_11 from "../assets/images/partners/partner_11.jpg";
import partner_12 from "../assets/images/partners/partner_12.jpg";
import partner_13 from "../assets/images/partners/partner_13.jpg";
import partner_14 from "../assets/images/partners/partner_14.jpg";
import partner_15 from "../assets/images/partners/partner_15.png";
import partner_16 from "../assets/images/partners/partner_16.jpg";
import partner_17 from "../assets/images/partners/partner_17.jpg";

const partnerLogos = [
  partner_1,  partner_2,  partner_3,  partner_4,  partner_5,
  partner_6,  partner_8,  partner_9,  partner_10,
  partner_11, partner_12, partner_13, partner_14, partner_15,
  partner_16, partner_17,
];

/* ── disable right-click ── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ── data ── */
const values = [
  { icon: iconTrust,   title: "Building With Trust",      desc: "Our multi-skilled team provides innovative, forward-thinking solutions tailored to each project's unique goals and challenges." },
  { icon: iconQuality, title: "Experience & Quality",     desc: "Our team delivers high-quality results through precise execution, strict standards, and a passion for getting it right the first time." },
  { icon: iconHealth,  title: "Health & Safety",          desc: "We prioritize strict safety standards to protect our people, clients, and communities — ensuring every site is secure, compliant, and risk-aware." },
  { icon: iconCrane,   title: "Commitment To Quality",    desc: "We uphold high quality standards from materials to delivery — ensuring durability, strong performance, and client satisfaction." },
];

/* ── Project References & Clients ── */
const refCategories = [
  {
    label: "MINING",
    items: [
      { client: "Global Tsax Consulting",            project: "SAMANCOR Mine – Mooi Nooi-Rustenburg" },
      { client: "Global Tsax Consulting",            project: "SAMANCOR Dikwena" },
      { client: "Mayibuye-I Afrika Construction",    project: "Sibanye Lonmin Mine" },
      { client: "G4 Mining & Civils",                project: "Matla Coal Mine – Screening & Crushing" },
      { client: "Tefla Group",                       project: "WATER INFRASTRUCTURE – Dikgalaope & Vredefort WWTW" },
      { client: "Tefla Group",                       project: "Arlington Pump Station" },
      { client: "Isiphethu Water Services",          project: "Ferrobank Waste Treatment Plant (ongoing)" },
      { client: "Udumo Trading",                     project: "LOWER THUKELA WATER TREATMENT WORKS" },
    ],
  },
  {
    label: "BUILDING PROJECTS",
    items: [
      { client: "ZMZEE Construction",                project: "Bakerton Library – Springs" },
      { client: "Waterfall Estate",                  project: "Waterfall Estate" },
      { client: "East Star Consulting",              project: "SANYO Trucking Boksburg" },
      { client: "Artrage Construction",              project: "Jeppe Library JHB" },
      { client: "Maximus Konstruksie",               project: "Lambton Garden Shopping Center" },
      { client: "TUT Residential",                   project: "Soshanguvhe" },
      { client: "Optimed",                           project: "Optimed – Queenwood" },
      { client: "Tecroveer",                         project: "PEPSICO – Isando" },
      { client: "Sealtec Coating",                   project: "Sunbake Pretoria West" },
      { client: "Unlimited Mind Projects",           project: "Mogalakwena Combined Reservoir 3.5ml" },
      { client: "Dawaila Group",                     project: "Dannhauser Reservoir – 3.5ml" },
      { client: "Kumula Investments",                project: "Petrus Steyn Reservoir – 3.5ml" },
      { client: "Kumula Investments",                project: "Ga-Motle Combined Reservoir 10ml" },
      { client: "Hexad Projects / JW Hugo Construction", project: "Aerosud" },
      { client: "MNT Consulting",                    project: "Steelpoort Bridge" },
    ],
  },
];

/* ── tabbed info panel ── */
const vidTabs = [
  {
    label: "About Bakwena Kit",
    img: tabImg1,
    title: "About Bakwena Kit",
    bullets: [
      "Bakwena Kit (Pty) Ltd is a proudly South African formwork and scaffolding operation that is 100% black owned and managed. Established in 2021, Bakwena Kit is an exciting new affiliate company to Kit Formwork and Scaffolding, that formerly operated under the name RMD Kwikform (Pty) Ltd.",
      "With a wholistic approach and broadened service offering required to design, supply and erect temporary works, we meet the demands of our customers within the expanding South African construction industry. Our branch footprint in Centurion, Cape Town, Durban, Mbombela and Bloemfontein, ensure a national service delivery.",
      "The dynamic shareholding team of Bakwena Kit underwrite their belief and declare their support in the growth and development of the South African construction industry. Our staff is well trained and equipped with the necessary skills and experience to complete even the most challenging of projects.",
    ],
  },
  {
    label: "Our Brand",
    img: tabImg2,
    title: "Our Brand",
    bullets: [
      "The Bakwena Kit brand is one that embodies the aspiration that we have as a Proudly South African enterprise. A new culture.",
      "It is about being passionate and ready to serve our customers in a new uncontested way.",
      "And yes, we are excited about being proudly South African.",
    ],
  },
  {
    label: "Our Promise",
    img: tabImg3,
    title: "Our Promise",
    bullets: [
      "We are deeply committed to the potential hidden on our continent and more particularly in our country.",
      "We will differentiate ourselves in the way we run our operations — establishing an unprecedented customer experience.",
      "We will build the future of this business on ethics and trust.",
    ],
  },
];

/* ─────────────────────────────────────────────────────────
   useVisible – returns how many cards to show per breakpoint
   ───────────────────────────────────────────────────────── */
function useVisibleCount(breakpoints) {
  // breakpoints: [{ maxWidth, count }, ...] ordered largest→smallest
  const get = useCallback(() => {
    const w = window.innerWidth;
    for (const bp of breakpoints) {
      if (w <= bp.maxWidth) return bp.count;
    }
    return breakpoints[0].count; // default (largest)
  }, [breakpoints]);

  const [count, setCount] = useState(get);

  useEffect(() => {
    const handler = () => setCount(get());
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [get]);

  return count;
}

/* ── References carousel component ── */
function RefsCarousel({ inView }) {
  const [catIndex, setCatIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);

  // Responsive: how many cards are visible at each breakpoint
  // Must match the CSS flex-basis values in About.css
  const VISIBLE = useVisibleCount([
    { maxWidth: 480,  count: 1 },
    { maxWidth: 768,  count: 2 },
    { maxWidth: 1200, count: 4 },
    { maxWidth: Infinity, count: 5 },
  ]);

  const cat = refCategories[catIndex];
  const maxItem = Math.max(0, cat.items.length - VISIBLE);

  // Reset scroll position when category or visible count changes
  useEffect(() => { setItemIndex(0); }, [catIndex, VISIBLE]);

  // Clamp itemIndex if VISIBLE count increases (e.g. screen rotates)
  useEffect(() => {
    setItemIndex(i => Math.min(i, maxItem));
  }, [maxItem]);

  const prevCat  = () => setCatIndex(i => (i - 1 + refCategories.length) % refCategories.length);
  const nextCat  = () => setCatIndex(i => (i + 1) % refCategories.length);
  const prevItem = () => setItemIndex(i => Math.max(0, i - 1));
  const nextItem = () => setItemIndex(i => Math.min(maxItem, i + 1));

  return (
    <section className="ab-refs">
      <div className="container">
        <div className={`ab-refs__head fade-in ${inView ? "visible" : ""}`}>
          <div className="ab-refs__tag">PROJECT REFERENCES &amp; CLIENTS</div>
          <h2 className="ab-refs__h2">Our Work Speaks<br /><span>For Itself</span></h2>
          <p className="ab-refs__sub">
            A selection of projects completed across mining, building, civil and water infrastructure sectors.
          </p>
        </div>
      </div>

      <div className={`ab-refs__tl-row fade-in ${inView ? "visible" : ""}`}>
        <div className="ab-refs__tl-line" />
        <div className="container ab-refs__cats">
          {refCategories.map((c, i) => (
            <button
              key={i}
              className={`ab-refs__cat-btn${catIndex === i ? " ab-refs__cat-btn--active" : ""}`}
              onClick={() => setCatIndex(i)}
              type="button"
            >
              <span className="ab-refs__cat-bubble">
                <span className="ab-refs__cat-num">0{i + 1}</span>
                <span className="ab-refs__cat-label">{c.label}</span>
              </span>
              <span className="ab-refs__cat-dot" />
            </button>
          ))}
        </div>
      </div>

      <div
        className={`ab-refs__carousel-outer fade-in ${inView ? "visible" : ""}`}
        style={{ transitionDelay: "0.15s" }}
      >
        <div className="ab-refs__side">
          <div className="ab-refs__side-line" />
          <button
            className="ab-refs__arrow"
            onClick={prevItem}
            disabled={itemIndex === 0}
            aria-label="Previous"
          >
            <FiChevronLeft size={22} />
          </button>
          <div className="ab-refs__side-line" />
        </div>

        <div className="ab-refs__viewport">
          <div
            className="ab-refs__track"
            style={{
              transform: `translateX(calc(-${itemIndex} * (100% / ${VISIBLE})))`,
            }}
          >
            {cat.items.map((item, j) => (
              <div
                key={`${catIndex}-${j}`}
                className={`ab-refs__card${j === 0 ? " ab-refs__card--active" : ""}`}
                style={{ flex: `0 0 calc(100% / ${VISIBLE})` }}
              >
                <div className="ab-refs__card-inner">
                  <span className="ab-refs__card-client">{item.client}</span>
                  <span className="ab-refs__card-project">{item.project}</span>
                </div>
                <span className="ab-refs__card-tail" aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>

        <div className="ab-refs__side">
          <div className="ab-refs__side-line" />
          <button
            className="ab-refs__arrow"
            onClick={nextItem}
            disabled={itemIndex >= maxItem}
            aria-label="Next"
          >
            <FiChevronRight size={22} />
          </button>
          <div className="ab-refs__side-line" />
        </div>
      </div>

      {/* Dot indicators */}
      {maxItem > 0 && (
        <div className="ab-refs__dots container">
          {Array.from({ length: maxItem + 1 }).map((_, i) => (
            <button
              key={i}
              className={`ab-refs__dot${itemIndex === i ? " ab-refs__dot--active" : ""}`}
              onClick={() => setItemIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      <div className="container ab-refs__cat-nav">
        <button className="ab-refs__cat-nav-btn" onClick={prevCat} type="button">
          <FiChevronLeft size={15} /> Previous Category
        </button>
        <span className="ab-refs__cat-active-label">{cat.label}</span>
        <button className="ab-refs__cat-nav-btn" onClick={nextCat} type="button">
          Next Category <FiChevronRight size={15} />
        </button>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════ */
export default function About() {
  useNoContextMenu();

  const [heroRef,  heroInView]  = useInView();
  const [introRef, introInView] = useInView();
  const [vidRef,   vidInView]   = useInView();
  const [valRef,   valInView]   = useInView();
  const [refsRef,  refsInView]  = useInView();

  const videoRef   = useRef(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    vid.play().catch(() => {});
  }, []);

  const tab = vidTabs[activeTab];

  return (
    <main className="ab">

      {/* ══════ 1. HERO ══════ */}
      <section className="ab-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="ab-hero__overlay" />
        <div className="container ab-hero__body" ref={heroRef}>
          <div className={`ab-hero__label fade-in ${heroInView ? "visible" : ""}`}>ABOUT US</div>
          <h1 className={`ab-hero__title fade-in ${heroInView ? "visible" : ""}`} style={{ transitionDelay: "0.1s" }}>
            BUILDING EXCELLENCE<br />SINCE 2021
          </h1>
        </div>
        <div className="ab-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>About Us</span>
        </div>
      </section>

      {/* ══════ 2. INTRO — Who We Are ══════ */}
      <section className="ab-intro" ref={introRef}>
        <div className="container ab-intro__inner">
          <div className={`ab-intro__img-col fade-in ${introInView ? "visible" : ""}`}>
            <img className="ab-intro__img-main" src={introImg} alt="Bakwena Kit construction team" loading="lazy" draggable="false" onDragStart={e => e.preventDefault()} />
            <div className="ab-intro__img-accent" aria-hidden="true" />
            <div className="ab-intro__icon-badge">
              <img src={iconLogo3} alt="Bakwena Kit logo" draggable="false" onDragStart={e => e.preventDefault()} />
            </div>
          </div>
          <div className={`ab-intro__right fade-in ${introInView ? "visible" : ""}`} style={{ transitionDelay: "0.15s" }}>
            <div className="ab-intro__tag">WHO WE ARE</div>
            <h2 className="ab-intro__h2">Elevating South Africa's<br /><span>Temporary Works</span> Industry</h2>
            <div className="ab-intro__quote">
              <div className="ab-intro__bar" />
              <p>Bakwena Kit (Pty) Ltd is a proudly South African formwork and scaffolding company. Our mission is to elevate the standard of support services and engineering expertise, positioning Bakwena Kit as a leading provider of temporary works solutions in both civil and building sectors.</p>
            </div>
            <div className="ab-intro__block">
              <div className="ab-intro__block-label">OUR NAME HISTORY</div>
              <p className="ab-intro__block-text">"The name Bakwena came from Bantwane tribe that based in the small rural area known as Ntwane which is found in the north-eastern part of South Africa."</p>
            </div>
            <div className="ab-intro__block">
              <div className="ab-intro__block-label">WE ARE PROUDLY SOUTH AFRICAN</div>
              <p className="ab-intro__block-text">"We are deeply committed to the potential hidden on our continent and more particularly in our country."</p>
            </div>
            <Link to="/services" className="ab-intro__cta">Our Services <FiArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* ══════ 3. PARTNER LOGOS MARQUEE ══════ */}
      <section className="ab-partners">
        <div className="ab-partners__label-bar">
          <div className="container">
            <span className="ab-partners__heading">PROJECT REFERENCES &amp; CLIENTS</span>
          </div>
        </div>
        <div className="ab-partners__marquee">
          <div className="ab-partners__track">
            {[...partnerLogos, ...partnerLogos].map((src, i) => (
              <div key={i} className="ab-partners__logo">
                <img
                  src={src}
                  alt={`Partner ${(i % partnerLogos.length) + 1}`}
                  loading="lazy"
                  draggable="false"
                  onDragStart={e => e.preventDefault()}
                  onContextMenu={e => e.preventDefault()}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ 4. VIDEO ══════ */}
      <section className="ab-video" ref={vidRef}>
        <div className="ab-video__bg">
          <video ref={videoRef} src={bgVideo} autoPlay muted loop playsInline preload="auto" />
        </div>
        <div className="ab-video__overlay" aria-hidden="true" />
        <div className={`container ab-video__headline fade-in ${vidInView ? "visible" : ""}`}>
          <div className="ab-video__left">
            <div className="ab-video__tag">SUPPORTING THE FUTURE</div>
            <h2 className="ab-video__h2">WE ARE PROUDLY<br /><span>SOUTH AFRICAN</span></h2>
            <p className="ab-video__sub">"We are deeply committed to the potential hidden on our continent and more particularly in our country."</p>
          </div>
        </div>
        <div className="ab-video__stripe" aria-hidden="true" />

        <div className="ab-vid-box__anchor">
          <div className="container ab-vid-box__wrapper">
            <div className={`ab-vid-box fade-in ${vidInView ? "visible" : ""}`} style={{ transitionDelay: "0.2s" }}>
              <div className="ab-vid-box__tabs">
                {vidTabs.map((t, i) => (
                  <button key={i} className={`ab-vid-box__tab ${activeTab === i ? "active" : ""}`} onClick={() => setActiveTab(i)} type="button">{t.label}</button>
                ))}
              </div>
              <div className="ab-vid-box__body">
                <div className="ab-vid-box__img-wrap">
                  <img src={tab.img} alt={tab.title} loading="lazy" draggable="false" onDragStart={e => e.preventDefault()} />
                </div>
                <div className="ab-vid-box__content">
                  <h3 className="ab-vid-box__title">{tab.title}</h3>
                  <ul className="ab-vid-box__list">
                    {tab.bullets.map((b, bi) => (
                      <li key={bi}><span className="ab-vid-box__check" aria-hidden="true"><FiCheck size={11} /></span>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════ 5. VALUES ══════ */}
      <section className="ab-values" ref={valRef}>
        <div className="ab-values__grid container">
          {values.map((v, i) => (
            <div key={i} className={`ab-val fade-in ${valInView ? "visible" : ""}`} style={{ transitionDelay: `${i * 0.1}s` }}>
              <h3 className="ab-val__title">{v.title}</h3>
              <div className="ab-val__icon-wrap"><img src={v.icon} alt={v.title} className="ab-val__icon" /></div>
              <p className="ab-val__desc">{v.desc}</p>
            </div>
          ))}
        </div>
        <div className="ab-values__stripe" aria-hidden="true" />
      </section>

      {/* ══════ 6. PROJECT REFERENCES & CLIENTS ══════ */}
      <div ref={refsRef}>
        <RefsCarousel inView={refsInView} />
      </div>

    </main>
  );
}